import React from "react";
import { MATERIAL_ORDERED_DUE_DAYS, RescheduleItem } from "@myapp/contract";
import { getReschedulingDetail } from "../../../../entities/schedule";
import { formatDayMonthDE } from "../../../../shared/helpers/date";

const KIND_LABELS: Record<RescheduleItem["kind"], string> = {
  RESCHEDULE: "Umdisponieren",
  FOLLOW_UP: "Folgetermin",
  MATERIAL_ORDERED: "Material bestellt",
};

/** The "why" of a row. Follow-ups carry the technician's `followUpReason`, the rest their cause. */
export function getRescheduleReason(item: RescheduleItem): string | null {
  if (item.kind === "FOLLOW_UP" || item.kind === "MATERIAL_ORDERED") {
    return item.followUpReason?.trim() || null;
  }
  return getReschedulingDetail(item.reschedulingCause, item.reschedulingReason, item.workStarted);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** „fällig ab 10.11." while the 14 days run; „seit 15 Tagen · fällig" once they are over. */
function getMaterialDue(item: RescheduleItem): { label: string; overdue: boolean } | null {
  if (!item.materialDueAt) return null;
  const dueAt = new Date(item.materialDueAt);
  if (dueAt.getTime() > Date.now()) {
    return { label: `fällig ab ${formatDayMonthDE(item.materialDueAt)}`, overdue: false };
  }
  const days = Math.floor((Date.now() - (dueAt.getTime() - MATERIAL_ORDERED_DUE_DAYS * DAY_MS)) / DAY_MS);
  return { label: `seit ${days} Tagen · fällig`, overdue: true };
}

/**
 * Kind in small type, reason beneath it. Two plain lines rather than a pill: the row already has
 * a coloured type badge, and the reason is what the dispatcher reads, so it gets the weight.
 */
export const RescheduleReason: React.FC<{ item: RescheduleItem; className?: string }> = ({
  item,
  className,
}) => {
  const reason = getRescheduleReason(item);
  const due = item.kind === "MATERIAL_ORDERED" ? getMaterialDue(item) : null;

  return (
    <span className={`flex flex-col gap-0.5 ${className ?? ""}`} title={reason ?? undefined}>
      <span
        className={`font-['Roboto'] text-[12px] font-medium leading-4 ${
          item.kind === "FOLLOW_UP" || due?.overdue ? "text-[#B42318]" : "text-gray-600"
        }`}
      >
        {KIND_LABELS[item.kind]}
      </span>
      {due && (
        <span
          className={`font-['Roboto'] text-[12px] leading-4 ${due.overdue ? "font-medium text-[#B42318]" : "text-gray-600"}`}
        >
          {due.label}
        </span>
      )}
      {reason && (
        <span className="font-['Roboto'] text-[14px] font-normal leading-[18px] text-gray-800">
          {reason}
        </span>
      )}
    </span>
  );
};
